import jsPDF from 'jspdf';
import autoTable, { type Cell, type CellDef, type CellInput, type Color, type UserOptions } from 'jspdf-autotable';
import { Report, AppSettings, ReportLanguage, PrintOptions, InspectionItem, ChecklistEntry, FreimeldungQrQuelle } from '../../types';
import { translateReportData } from '../geminiService';
import { kategorieText, pruefpunktText } from '../checklistSprache';
import { resolveFooterText } from '../profileSettings';
import { TRANSLATIONS, sanitizeRole, hexToRgb, optimizePhotoForPdf, optimizePlanForPdf, formatDate, countInspectionStats, selectActiveItemsForReport, selectItemsForCompanyStats, selectItemsOnPlan, erledigteBilderNurSeitVorbericht, erledigteNachAlterRegel, vorberichtsDatum, teileErledigteFuerPdf, bilderFuerPdf, planeFuerPdf, type BerichtsKopf, blickpfeil, blickpfeilLaengeAnNadel, zeichneStecknadel, zeichneStecknadelSchatten, stecknadelGeometrie, planBildRahmenMitNadeln, nadelUeberstandOben, PLAN_SEITE, KOPFZEILE_LINIE_Y, BLICKPFEIL_STRICH_MM, STECKNADEL_KUGEL_MM, STECKNADEL_GRUPPEN_FAKTOR } from './pdfUtils';
import { pinsAufPlan } from '../planPins';
import { nadelNeigungen } from '../planNadelNeigung';
import { fitLogoBox, imgFmt } from './letterhead';
import { ensureReportFont, reportFontFaces, type ReportFontRole } from './reportFont';
// Nur der Typ — der eigentliche cloudService-Import bleibt weiter unten dynamisch
// (Code-Splitting), dieser Import verschwindet beim Kompilieren komplett.
import type { ReadMigrationOptions } from '../cloudService';

/**
 * `options.skipMigration` bleibt fuer die Aufrufer erhalten, wirkt hier aber
 * nicht mehr: seit 30.09.2026 liest der Generator nur noch die Kopfliste des
 * Archivs (fetchReportHistoryKoepfe — Nummer, Abschnitt, Datum), und die
 * schreibt nie etwas zurueck. Aus der History wird ohnehin ausschliesslich
 * das Datum des Vorberichts abgeleitet.
 *
 * `options.signal`: bricht ein veralteter Lauf ab (Vorschau neu angestossen),
 * wirft der Generator zwischen den teuren Schritten einen `AbortError`,
 * statt weiter Bilder zu rechnen, deren Ergebnis niemand mehr anzeigt.
 *
 * `options.conductedByName`: der eingeloggte Mensch, der den Bericht gerade
 * schreibt/versendet. Geht vor `metadata.conductedByName` (Archiv-Stempel).
 *
 * `options.freimeldungQr`: der QR-Code, mit dem Empfaenger des PDFs einen
 * Mangel ohne Login als behoben melden. Fehlt er (kein Link, Migration offen,
 * Vorschau vor dem ersten Versand), entsteht KEIN Platzhalter — das PDF sieht
 * dann exakt aus wie bisher.
 */
export interface GeneratePdfOptions extends ReadMigrationOptions {
  conductedByName?: string;
  freimeldungQr?: FreimeldungQrQuelle | null;
  signal?: AbortSignal;
}

// --- Gestaltung (Betreiber-Freigabe 18.10.2026) ------------------------------
// Inter, viel Weissraum, wenig Farbflaechen; jeder Mangel steht genau EINMAL im
// Protokoll (keine Uebersichtstabelle, kein Handlungsbedarf-Kasten) — die
// Dringlichkeit ergibt sich aus der Gliederung „Sofort handeln“ / „Offene
// Maengel“ / „Hinweise und positive Feststellungen“.

type RGB = [number, number, number];

/** Seitenrand links/rechts (mm), auch auf den Planseiten. */
export const SEITENRAND_MM = 18;
const INHALT_BREITE = 210 - 2 * SEITENRAND_MM;
/** Oberkante des Inhalts unter der laufenden Kopfzeile. */
const INHALT_OBEN = 25;
/** Abstand der Inhaltsunterkante zum Blattrand (Fusslinie bei H−13). */
const INHALT_UNTEN_FREI = 18;

const KARTE_LEISTE = 1.3;
const KARTE_PAD = 5.5;
const KARTE_INNEN = INHALT_BREITE - KARTE_LEISTE - 2 * KARTE_PAD;
const FOTO_ABSTAND = 3.5;
/**
 * Fotos einer Reihe (bis zu drei) stehen gleich hoch nebeneinander, jedes in
 * seinem eigenen Seitenverhaeltnis — Hochformat bleibt hochkant, nichts wird
 * beschnitten oder mit Weiss aufgefuellt (Betreiber 18.10.2026: im 4:3-Feld
 * bekam ein Hochformatfoto breite weisse Raender). Die Reihe fuellt hoechstens
 * die Kartenbreite und wird hoechstens so hoch.
 */
export const KARTEN_FOTO_MAX_HOEHE_MM = 60;
/** Lange Kante eines Kartenfotos im PDF (Pixel). */
const FOTO_MAX_KANTE_PX = 1000;
/** Hoechstens so viele Fotos je Karte (wie bisher), dazu ggf. das Beweisbild. */
const FOTOS_JE_KARTE = 3;

const FARBE: Record<'ink' | 'text' | 'muted' | 'faint' | 'line' | 'lineStrong' | 'surface' | 'brand' | 'brandLine' | 'white', RGB> = {
  ink: [17, 24, 39],
  text: [55, 65, 81],
  muted: [107, 114, 128],
  faint: [156, 163, 175],
  line: [229, 231, 235],
  lineStrong: [209, 213, 219],
  surface: [248, 250, 252],
  brand: [29, 78, 216],
  brandLine: [191, 219, 254],
  white: [255, 255, 255],
};

interface StatusFarbe { c: RGB; tint: RGB }
const STATUS: Record<InspectionItem['status'], StatusFarbe> = {
  open: { c: [220, 38, 38], tint: [254, 242, 242] },
  info: { c: [217, 119, 6], tint: [255, 247, 237] },
  good: { c: [5, 150, 105], tint: [236, 253, 245] },
  closed: { c: [22, 163, 74], tint: [240, 253, 244] },
  general: { c: [37, 99, 235], tint: [239, 246, 255] },
};

/** Tagesdatum 'JJJJ-MM-TT' aus einem ISO-Text oder Datumstext; null wenn unlesbar. */
const tagVon = (wert?: string | null): string | null => {
  if (!wert) return null;
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(wert);
  if (iso) return iso[1];
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};

/** Frist vor dem Begehungstag = ueberschritten. Ohne Frist oder Datum nie. */
export const fristUeberschritten = (frist?: string | null, berichtsDatum?: string | null): boolean => {
  const f = tagVon(frist);
  const b = tagVon(berichtsDatum);
  return f !== null && b !== null && f < b;
};

export type Dringlichkeit = 'sofort' | 'offen' | 'hinweise';

/**
 * In welchen Abschnitt gehoert ein Eintrag? Offen mit Sofortmassnahme oder
 * ueberschrittener Frist → „Sofort handeln“; uebrige offene → „Offene
 * Maengel“; Hinweis und Positiv → „Hinweise und positive Feststellungen“.
 * Information und Erledigtes stehen woanders (null).
 */
export const dringlichkeit = (
  item: Pick<InspectionItem, 'status' | 'immediateAction' | 'deadline'>,
  berichtsDatum?: string | null,
): Dringlichkeit | null => {
  if (item.status === 'open') return item.immediateAction || fristUeberschritten(item.deadline, berichtsDatum) ? 'sofort' : 'offen';
  if (item.status === 'info' || item.status === 'good') return 'hinweise';
  return null;
};

/** Eine Textzeile in einer Tabellenzelle mit eigener Schrift (Titel + Unterzeile). */
interface ZellZeile { t: string; rolle: ReportFontRole; size: number; farbe: RGB; mitte?: boolean }
/** Zellinhalt, den der Generator selbst zeichnet (autoTable misst nur). */
type EigeneZelle = CellDef & { zeilen?: ZellZeile[]; zeichne?: (cell: Cell) => void };

/**
 * `globalHistory`: das Berichtsarchiv, falls der Aufrufer es schon hat — volle
 * Eintraege oder Listenformat, gebraucht werden nur Nummer, Abschnitt und
 * Datum. Fehlt es, wird bei Bedarf die schlanke Kopfliste nachgeladen.
 */
export const generatePDF = async (report: Report, settings?: AppSettings, globalHistory?: readonly BerichtsKopf[], overrideLang?: ReportLanguage, options?: GeneratePdfOptions) => {
  const lang: ReportLanguage = overrideLang || report.metadata.reportLanguage || 'de';
  const isHseMode = settings?.appMode === 'hse';

  const defaultOptions: PrintOptions = {
    includeDistribution: true, includeChecklists: true, includeActiveItems: true,
    includeHistory: true, includeReportHistory: true, includePlans: true,
    plansLandscape: false, includeStatistics: true, includeHazards: true,
    // Der Freimelde-Kasten wird gedruckt, solange ihn niemand bewusst abwaehlt.
    showFreimeldungQr: true
  };
  const printOptions = { ...defaultOptions, ...(report.metadata.printOptions || {}) };
  // Betreiber-Entscheid 30.09.2026: aeltere Erledigte ohne Bilder ist die
  // Vorgabe fuer alle Projekte — Aufloesung und Begruendung (neuer Schluessel
  // statt `hideClosedImagesAfterNextReport`) in pdfUtils.
  const nurSeitVorbericht = erledigteBilderNurSeitVorbericht(report.metadata.printOptions, report.metadata.date);

  // Ein veralteter Lauf (Vorschau neu angestossen) soll nicht noch hunderte
  // Bilder rechnen: zwischen den teuren Schritten wird nachgesehen.
  const abbruchPruefen = () => {
    if (options?.signal?.aborted) {
      const e = new Error('PDF-Erzeugung abgebrochen');
      e.name = 'AbortError';
      throw e;
    }
  };

  // Die Schrift laedt parallel zu Uebersetzung und Bildern (eigener Chunk).
  const schriftGeladen = ensureReportFont();

  // Findings are authored directly in English when the report language is English
  // (the finding text is generated in the report's language at input time), and all
  // fixed labels come from the static t()/TRANSLATIONS dictionary. A plain 'en'
  // report therefore needs no AI translation at all -> the PDF/send is instant.
  // Only the bilingual 'de/en' mode still calls translateReportData to add the
  // second language (and that call is cached, so it stays cheap too).
  const reportToPrint = (lang === 'de/en')
    ? await translateReportData(report, lang)
    : report;
  abbruchPruefen();

  // Erst festlegen, WAS gedruckt wird, dann nur dafuer Bilder rechnen.
  // „Information" zuerst, danach wie bisher nach Ticketnummer absteigend.
  const activeItems = selectActiveItemsForReport(reportToPrint.items);

  let historyItems = reportToPrint.items.filter(i => i.status === 'closed');
  if (printOptions.historyFilterDays && printOptions.historyFilterDays > 0) {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - printOptions.historyFilterDays);
      historyItems = historyItems.filter(item => {
          if (!item.closedDate) return false;
          const d = new Date(item.closedDate);
          return d >= cutoffDate;
      });
  }

  // Das Vorberichtsdatum entscheidet nur, ob ein Erledigter mit Beweisbild
  // eine Karte bekommt oder in die Tabelle rutscht. Nachgeschlagen wird also
  // nur, wenn die Regel an ist und es ueberhaupt einen solchen Kandidaten gibt.
  // Frueher lud das fetchReportHistory — alle Archiv-Schnappschuesse des
  // Projekts (bei 6051 zig MB), nur um daraus ein Datum zu lesen. Die
  // Kopfliste traegt genau Nummer, Abschnitt und Datum.
  let previousReportDate: string | null = null;
  if (nurSeitVorbericht && printOptions.includeHistory && historyItems.some(i => i.closedImage)) {
      let koepfe: readonly BerichtsKopf[] | undefined = globalHistory;
      if (!koepfe || koepfe.length === 0) {
          const projectId = report.metadata.globalData?.projectId;
          if (projectId) {
              try {
                  const { fetchReportHistoryKoepfe } = await import('../cloudService');
                  koepfe = await fetchReportHistoryKoepfe(projectId);
              } catch (e) {
                  // Ohne Archiv kein Vorbericht: dann behalten alle Erledigten
                  // ihre Karte — lieber zu viel gedruckt als ein Nachweis weg.
                  console.error("Failed to fetch history for checking closed items", e);
              }
          }
      }
      previousReportDate = vorberichtsDatum(koepfe, report.metadata.inspectionNumber, report.metadata.sectionId);
      abbruchPruefen();
  }
  const { karten: historyWithProof, tabelle: historySimple } =
      teileErledigteFuerPdf(historyItems, nurSeitVorbericht, previousReportDate,
        erledigteNachAlterRegel(report.metadata.printOptions, report.metadata.date));
  const relevantPlans = planeFuerPdf(reportToPrint.metadata.plans, reportToPrint.items, reportToPrint.safetyPoints, printOptions);

  const optimizedPhotos = new Map<string, string[]>();
  const optimizedPlans = new Map<string, string>();
  const optimizedClosedImages = new Map<string, string>();


  // Bildoptimierung ist der Rechenkern und dominiert die Laufzeit bei großen
  // Berichten. Statt Item-für-Item seriell abzuarbeiten, werden alle Aufgaben
  // gesammelt und mit begrenzter Nebenläufigkeit parallel ausgeführt. Ergebnis
  // landet unverändert in denselben Maps (Reihenfolge irrelevant, da per id).
  // Nur was auf einer Karte gezeichnet wird: aktuelle Feststellungen und
  // Historien-Karten (bilderFuerPdf), dazu die Pläne mit Planseite.
  const imageTasks: Array<() => Promise<void>> = [];
  const { fotos, beweisbilder } = bilderFuerPdf([
      ...(printOptions.includeActiveItems ? activeItems : []),
      ...(printOptions.includeHistory ? historyWithProof : []),
  ]);

  for (const { id, quellen } of fotos) {
      imageTasks.push(async () => {
          try {
              const opResults = await Promise.all(
                  quellen.map(p => optimizePhotoForPdf(p, FOTO_MAX_KANTE_PX, 0.75))
              );
              optimizedPhotos.set(id, opResults);
          } catch(e) { console.warn('Failed to optimize photo', e); }
      });
  }
  for (const { id, quelle } of beweisbilder) {
      imageTasks.push(async () => {
          try {
              const opt = await optimizePhotoForPdf(quelle, FOTO_MAX_KANTE_PX, 0.75);
              optimizedClosedImages.set(id, opt);
          } catch(e) { console.warn('Failed to optimize closed image', e); }
      });
  }

  for (const plan of relevantPlans) {
      imageTasks.push(async () => {
          try {
              // Planseite ist A4 quer und voll genutzt: lange Kante
              // 2800 px (~300 dpi), sonst wirken Linien und
              // Beschriftungen des Plans unscharf.
              const opt = await optimizePlanForPdf(plan.image, 2800, 0.7);
              optimizedPlans.set(plan.id, opt);
          } catch(e) { console.warn('Failed to optimize plan', e); }
      });
  }

  // Begrenzte Nebenläufigkeit: deutlich schneller als seriell, ohne Speicher/
  // Sockets bei sehr vielen Bildern zu überlasten.
  const IMG_CONCURRENCY = 8;
  for (let i = 0; i < imageTasks.length; i += IMG_CONCURRENCY) {
      abbruchPruefen();
      await Promise.all(imageTasks.slice(i, i + IMG_CONCURRENCY).map(fn => fn()));
  }
  await schriftGeladen;
  abbruchPruefen();

  const doc = new jsPDF({ compress: true });
  const SCHRIFT = reportFontFaces(doc);
  const meta = reportToPrint.metadata;
  const effectiveLang = (lang === 'de/en') ? 'en' : lang;

  const t = (key: string) => {
    const val = TRANSLATIONS[key]?.[effectiveLang] || key;
    if (isHseMode) {
        if (key === 'reportTitle') return effectiveLang === 'de' ? 'HSE Bericht' : 'HSE Report';
        if (key === 'sigeko') return effectiveLang === 'de' ? 'HSE Manager' : 'HSE Manager';
        if (key === 'deputy') return effectiveLang === 'de' ? 'Stellv. HSE' : 'Deputy HSE';
    }
    return val;
  };

  // --- Zeichenwerkzeuge -------------------------------------------------------
  const seitenBreite = () => doc.internal.pageSize.getWidth();
  const seitenHoehe = () => doc.internal.pageSize.getHeight();

  const F = (rolle: ReportFontRole, size: number, farbe?: RGB) => {
    const [familie, schnitt] = SCHRIFT[rolle];
    doc.setFont(familie, schnitt);
    doc.setFontSize(size);
    if (farbe) doc.setTextColor(farbe[0], farbe[1], farbe[2]);
  };
  const fuellung = (c: RGB) => doc.setFillColor(c[0], c[1], c[2]);
  const linie = (c: RGB, breite = 0.25) => { doc.setDrawColor(c[0], c[1], c[2]); doc.setLineWidth(breite); };
  const umbrechen = (text: string, breite: number): string[] => doc.splitTextToSize(text, breite) as string[];

  // Fusszeile: gepflegter Text schlaegt den Rueckfall "Firma | Anschrift".
  // resolveFooterText statt ||, damit ein bewusst geleertes Feld ('') leer
  // bleibt — mit || stuende die Anschrift sofort wieder da und die
  // Fusszeile liesse sich nicht abschalten.
  const footerParts: string[] = [];
  if (settings?.companyName) footerParts.push(settings.companyName);
  if (settings?.companyAddress) footerParts.push(settings.companyAddress.replace(/\n/g, ', '));
  const footerText = resolveFooterText(settings?.footerText, footerParts.join(' | ')) ?? '';
  const FUSS_ZEILENABSTAND = 3.2;
  const fussZeilen = (blattBreite: number): string[] => {
    if (!footerText) return [];
    F('regular', 6.8);
    return umbrechen(footerText, blattBreite - 2 * SEITENRAND_MM - 30);
  };
  // Eine mehrzeilige Fusszeile waechst nach oben: der Inhalt muss ihr vorher
  // Platz lassen, sonst laufen Karten, Tabellen oder der Plan hinein.
  const fussZusatz = (blattBreite: number) => Math.max(0, fussZeilen(blattBreite).length - 1) * FUSS_ZEILENABSTAND;
  const FUSS_ZUSATZ_HOCH = fussZusatz(210);
  const FUSS_ZUSATZ_QUER = fussZusatz(297);
  const inhaltUnten = () => seitenHoehe() - INHALT_UNTEN_FREI - FUSS_ZUSATZ_HOCH;
  /** Kleine, gesperrte Beschriftung in Grossbuchstaben (ORT, ZUSTÄNDIG …). */
  const beschriftung = (text: string, x: number, y: number, farbe: RGB = FARBE.muted, size = 6.6) => {
    F('semibold', size, farbe);
    doc.setCharSpace(0.3);
    doc.text(text.toUpperCase(), x, y);
    doc.setCharSpace(0);
  };
  /** Kuerzt auf die Breite und haengt „…“ an (Schrift vorher setzen). */
  const kuerzen = (text: string, breite: number): string => {
    if (doc.getTextWidth(text) <= breite) return text;
    let s = text;
    while (s.length > 1 && doc.getTextWidth(s + '…') > breite) s = s.slice(0, -1);
    return s.trimEnd() + '…';
  };
  const haken = (x: number, y: number, groesse: number, farbe: RGB, breite = 0.5) => {
    linie(farbe, breite);
    doc.setLineCap('round'); doc.setLineJoin('round');
    doc.lines([[groesse * 0.34, groesse * 0.34], [groesse * 0.66, -groesse * 0.78]], x, y);
    doc.setLineCap('butt'); doc.setLineJoin('miter');
  };
  const plaketteBreite = (text: string) => { F('semibold', 6.8); return doc.getTextWidth(text) + 5.6; };
  /** Status-Plakette: getoente Pille mit Punkt; `yMitte` ist die senkrechte Mitte. */
  const plakette = (x: number, yMitte: number, s: StatusFarbe, text: string) => {
    const w = plaketteBreite(text);
    fuellung(s.tint); doc.roundedRect(x, yMitte - 2.4, w, 4.8, 2.4, 2.4, 'F');
    fuellung(s.c); doc.circle(x + 2.3, yMitte, 0.7, 'F');
    F('semibold', 6.8, s.c); doc.text(text, x + 3.7, yMitte + 0.85);
  };
  const statusText = (status: InspectionItem['status']) =>
    status === 'open' ? t('statusOpen') : status === 'info' ? t('statusInfo') : status === 'good' ? t('statusGood') : status === 'general' ? t('statusGeneral') : t('statusClosed');

  // E-Mail-Adressen haben keine Leerzeichen: splitTextToSize bricht sie sonst
  // mitten im Wort um ("...firma.d" / "e"). Passt die Adresse nicht in die
  // Breite, wird nach dem @ getrennt - der einzige Umbruch, der lesbar bleibt.
  // Misst mit der gerade gesetzten Schrift, also vorher Groesse setzen.
  const wrapEmail = (email: string, maxWidth: number): string[] => {
    if (doc.getTextWidth(email) <= maxWidth) return [email];
    const at = email.indexOf('@');
    if (at <= 0) return umbrechen(email, maxWidth);
    return [email.slice(0, at + 1), email.slice(at + 1)];
  };
  const looksLikeEmail = (text: string) => text.includes('@') && !/\s/.test(text);

  // fitLogoBox statt reiner Hoehenskalierung: ein breites Logo wuerde sonst
  // breiter als das Blatt und begaenne bei negativem x. Format aus der
  // DataURL, nicht hart 'JPEG'.
  const logo = settings?.companyLogo;
  const logoMasse = (() => {
    if (!logo) return null;
    try { return doc.getImageProperties(logo); } catch { return null; }
  })();
  /** Zeichnet das Logo unten buendig in die Box; liefert die Breite (0 = keins). */
  const zeichneLogo = (x: number, y: number, maxW: number, maxH: number, rechtsbuendig: boolean): number => {
    if (!logo || !logoMasse) return 0;
    const box = fitLogoBox(logoMasse, maxW, maxH);
    if (!box) return 0;
    try {
      doc.addImage(logo, imgFmt(logo), rechtsbuendig ? x - box.width : x, y + (maxH - box.height), box.width, box.height, undefined, 'FAST');
    } catch { return 0; }
    return box.width;
  };

  const firmenName = settings?.companyName || '';
  const abschnittName = meta.sectionId ? meta.globalData?.sections?.find(s => s.id === meta.sectionId)?.name : undefined;
  const projektZeile = abschnittName ? `${meta.projectName} – ${abschnittName}` : (meta.projectName || '');

  // --- Laufende Kopfzeile (ab Seite 2, auch quer) -------------------------------
  const seitenMitKopf = new Set<number>([1]);
  const laufenderKopf = () => {
    const seite = doc.getCurrentPageInfo().pageNumber;
    if (seitenMitKopf.has(seite)) return;
    seitenMitKopf.add(seite);
    const w = seitenBreite();
    let rechts = zeichneLogo(w - SEITENRAND_MM, 8.6, 30, 6, true);
    if (!rechts && firmenName) {
      F('semibold', 8, FARBE.brand);
      const name = kuerzen(firmenName, 60);
      doc.text(name, w - SEITENRAND_MM, 12.7, { align: 'right' });
      rechts = doc.getTextWidth(name);
    }
    const titel = `${t('reportTitle')} ${t('reportNr')} ${meta.inspectionNumber}`;
    F('semibold', 7.5, FARBE.ink); doc.text(titel, SEITENRAND_MM, 12.5);
    const tw = doc.getTextWidth(titel);
    F('regular', 7.5, FARBE.muted);
    // Datum vor dem Projektnamen: wird gekuerzt, dann nur der Name.
    const rest = `   ·   ${formatDate(meta.date)}   ·   ${projektZeile}`;
    doc.text(kuerzen(rest, w - 2 * SEITENRAND_MM - tw - rechts - 6), SEITENRAND_MM + tw, 12.5);
    linie(FARBE.line); doc.line(SEITENRAND_MM, KOPFZEILE_LINIE_Y, w - SEITENRAND_MM, KOPFZEILE_LINIE_Y);
  };

  let cy = INHALT_OBEN;
  // jsPDF uebernimmt bei addPage() ohne Argumente Format UND Ausrichtung der
  // letzten Seite — nach den Planseiten muss Hochformat ausdruecklich her.
  const neueSeite = (ausrichtung: 'portrait' | 'landscape' = 'portrait') => {
    doc.addPage('a4', ausrichtung);
    laufenderKopf();
    cy = INHALT_OBEN;
  };
  const platz = (hoehe: number) => { if (cy + hoehe > inhaltUnten()) neueSeite(); };
  /** Abschnittsueberschrift; nie allein am Seitenende (mindestDanach = erster Block). */
  const abschnitt = (titel: string, mindestDanach = 30, punkt?: RGB) => {
    platz(11 + mindestDanach);
    let x = SEITENRAND_MM;
    if (punkt) { fuellung(punkt); doc.circle(SEITENRAND_MM + 1.5, cy + 3.1, 1.5, 'F'); x += 5.5; }
    F('semibold', 13, FARBE.ink); doc.text(titel, x, cy + 4.5);
    cy += 11;
  };

  // --- Tabellen: nur Haarlinien, Kopf klein und grau --------------------------
  /** Zelle mit mehreren Zeilen in eigener Schrift. Gemessen wird mit der breitesten. */
  const zeilenZelle = (zeilen: ZellZeile[]): CellDef => {
    const gewicht = (r: ReportFontRole) => (r === 'semibold' ? 2 : r === 'medium' ? 1 : 0);
    const breiteste = zeilen.reduce((a, z) => (gewicht(z.rolle) > gewicht(a.rolle) ? z : a), zeilen[0]);
    const groesste = Math.max(...zeilen.map(z => z.size));
    const [font, fontStyle] = SCHRIFT[breiteste.rolle];
    return { content: zeilen.map(z => z.t).join('\n'), styles: { font, fontStyle, fontSize: groesste }, zeilen } as EigeneZelle;
  };
  const zeichenZelle = (zeichne: (cell: Cell) => void): CellDef => ({ content: ' ', zeichne } as EigeneZelle);
  const zeilenZeichnen = (cell: Cell, zeilen: ZellZeile[]) => {
    const breite = cell.width - cell.padding('left') - cell.padding('right');
    const gesetzt: Array<ZellZeile & { h: number }> = [];
    zeilen.forEach(z => { F(z.rolle, z.size); umbrechen(z.t, breite).forEach(l => gesetzt.push({ ...z, t: l, h: z.size * 0.3528 * 1.18 })); });
    const gesamt = gesetzt.reduce((a, z) => a + z.h, 0);
    let y = cell.y + Math.max(cell.padding('top') - 0.6, (cell.height - gesamt) / 2);
    gesetzt.forEach(z => {
      F(z.rolle, z.size, z.farbe);
      if (z.mitte) doc.text(z.t, cell.x + cell.width / 2, y + z.h * 0.78, { align: 'center' });
      else doc.text(z.t, cell.x + cell.padding('left'), y + z.h * 0.78);
      y += z.h;
    });
  };
  const tabelle = (kopf: string[], zeilen: CellInput[][], weitere: UserOptions = {}) => {
    autoTable(doc, {
      startY: cy, head: [kopf], body: zeilen, theme: 'plain',
      margin: { left: SEITENRAND_MM, right: SEITENRAND_MM, top: INHALT_OBEN, bottom: INHALT_UNTEN_FREI + FUSS_ZUSATZ_HOCH },
      styles: { font: SCHRIFT.regular[0], fontStyle: SCHRIFT.regular[1] as 'normal', fontSize: 8, textColor: FARBE.text as Color, cellPadding: { top: 2.6, bottom: 2.6, left: 1.8, right: 1.8 }, lineColor: FARBE.line as Color, lineWidth: { bottom: 0.2 }, valign: 'middle', overflow: 'linebreak' },
      headStyles: { font: SCHRIFT.semibold[0], fontStyle: SCHRIFT.semibold[1] as 'normal', fontSize: 6.6, textColor: FARBE.muted as Color, lineColor: FARBE.lineStrong as Color, lineWidth: { bottom: 0.35 }, cellPadding: { top: 1.2, bottom: 2.2, left: 1.8, right: 1.8 } },
      willDrawCell: d => {
        const roh = d.cell.raw as EigeneZelle | undefined;
        if (d.section === 'body' && roh && typeof roh === 'object' && (roh.zeilen || roh.zeichne)) d.cell.text = [];
      },
      didDrawCell: d => {
        if (d.section !== 'body') return;
        const roh = d.cell.raw as EigeneZelle | undefined;
        if (!roh || typeof roh !== 'object') return;
        if (roh.zeilen) zeilenZeichnen(d.cell, roh.zeilen);
        if (roh.zeichne) roh.zeichne(d.cell);
      },
      didDrawPage: () => laufenderKopf(),
      ...weitere,
    });
    cy = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 11;
  };
  const kopfMittig = (abSpalte: number, bisSpalte = 99): UserOptions['didParseCell'] => d => {
    if (d.section === 'head' && d.column.index >= abSpalte && d.column.index <= bisSpalte) d.cell.styles.halign = 'center';
  };

  const bild = (quelle: string, x: number, y: number, w: number, h: number, rahmen: RGB = FARBE.line, rahmenBreite = 0.2) => {
    try {
      doc.addImage(quelle, 'JPEG', x, y, w, h, undefined, 'FAST');
      linie(rahmen, rahmenBreite); doc.rect(x, y, w, h, 'S');
    } catch(e) { console.warn("Fancy image error", e); }
  };
  /**
   * Seitenverhaeltnis Hoehe ÷ Breite eines Fotos. Nur eingebettete Daten werden
   * vermessen — eine rohe URL (Optimierung gescheitert) wuerde jsPDF synchron
   * nachladen; dann und bei unlesbaren Daten gilt 3:4 quer.
   */
  const verhaeltnisCache = new Map<string, number>();
  const hoeheZuBreite = (quelle: string): number => {
    const bekannt = verhaeltnisCache.get(quelle);
    if (bekannt !== undefined) return bekannt;
    let v = 0.75;
    if (quelle.startsWith('data:image/')) {
      try {
        const p = doc.getImageProperties(quelle);
        if (p.width > 0 && p.height > 0) v = p.height / p.width;
      } catch { /* bleibt 3:4 */ }
    }
    verhaeltnisCache.set(quelle, v);
    return v;
  };

  // ============================================================================
  // Seite 1: Kopf als Raster — Beschriftung klein darueber, Wert darunter
  // ============================================================================
  {
    const w = seitenBreite();
    const logoBreite = zeichneLogo(SEITENRAND_MM, 12, 70, 14, false);
    if (!logoBreite && firmenName) {
      F('semibold', 16, FARBE.brand);
      doc.text(kuerzen(firmenName, INHALT_BREITE / 2), SEITENRAND_MM, 24);
    }
    let ay = 17;
    // Ohne Logo steht der Name schon links gross — rechts nur die Anschrift.
    if (logoBreite && firmenName) { F('semibold', 7.4, FARBE.ink); doc.text(firmenName, w - SEITENRAND_MM, ay, { align: 'right' }); ay += 3.6; }
    F('regular', 7.2, FARBE.muted);
    (settings?.companyAddress || '').split(/[\n|]/).map(s => s.trim()).filter(Boolean)
      .forEach(zeile => { doc.text(zeile, w - SEITENRAND_MM, ay, { align: 'right' }); ay += 3.6; });

    let y = 42;
    beschriftung(`${t('reportTitle')}  ·  ${t('reportNr')} ${meta.inspectionNumber}`, SEITENRAND_MM, y, FARBE.brand, 8);
    y += 11;
    F('semibold', 23, FARBE.ink);
    const titelZeilen = umbrechen(meta.projectName || '-', INHALT_BREITE);
    titelZeilen.forEach((l, i) => doc.text(l, SEITENRAND_MM, y + i * 9.4));
    y += (titelZeilen.length - 1) * 9.4;
    if (abschnittName) { y += 8; F('regular', 12.5, FARBE.muted); doc.text(abschnittName, SEITENRAND_MM, y); }
    y += 8;

    // Schreibt nicht der Hauptverantwortliche den Bericht, steht der
    // tatsaechliche Durchfuehrende im Protokoll - egal ob fester Vertreter
    // oder Urlaubsuebernahme. Beim Versand kommt der Name aus dem Login,
    // im Archiv aus dem Snapshot (conductedByName).
    const norm = (s?: string) => (s || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const conductedByName = (options?.conductedByName || meta.conductedByName || '').trim();
    const vertreten = !!conductedByName && !!meta.coordinator && norm(conductedByName) !== norm(meta.coordinator);
    const durch = conductedByName || meta.coordinator || '-';
    const durchUnter = vertreten ? `${t('sigeko')}: ${meta.coordinator}` : t('sigeko');
    const datum = meta.date ? new Date(meta.date) : null;
    const wochentag = datum && !Number.isNaN(datum.getTime())
      ? datum.toLocaleDateString(effectiveLang === 'de' ? 'de-DE' : 'en-GB', { weekday: 'long' }) : '';

    // Werte brechen um statt abgeschnitten zu werden: Projekt-Nr. und Namen
    // identifizieren das Protokoll. Die Zeile waechst mit dem laengsten Feld.
    const spalte = INHALT_BREITE / 4;
    const felder = [
      { label: t('dateTime'), wert: formatDate(meta.date) || '-', unter: wochentag },
      { label: t('time'), wert: meta.time ? (effectiveLang === 'de' ? `${meta.time} Uhr` : meta.time) : '-', unter: '' },
      { label: t('projectNr'), wert: meta.projectNumber || '-', unter: '' },
      { label: t('inspectedBy'), wert: durch, unter: durchUnter },
    ].map(f => {
      F('medium', 9.2); const werte = umbrechen(f.wert, spalte - 4);
      F('regular', 7.2); const unter = f.unter ? umbrechen(f.unter, spalte - 4) : [];
      return { ...f, werte, unterZeilen: unter };
    });
    const feldHoehe = (f: (typeof felder)[number]) =>
      10.4 + (f.werte.length - 1) * 4 + (f.unterZeilen.length ? 3.8 + (f.unterZeilen.length - 1) * 3.3 : 0) + 3.3;
    linie(FARBE.line); doc.line(SEITENRAND_MM, y, w - SEITENRAND_MM, y);
    felder.forEach((f, i) => {
      const x = SEITENRAND_MM + i * spalte;
      beschriftung(f.label, x, y + 5.4, FARBE.faint, 5.9);
      F('medium', 9.2, FARBE.ink); f.werte.forEach((l, j) => doc.text(l, x, y + 10.4 + j * 4));
      const unterOben = y + 10.4 + (f.werte.length - 1) * 4 + 3.8;
      F('regular', 7.2, FARBE.muted); f.unterZeilen.forEach((l, j) => doc.text(l, x, unterOben + j * 3.3));
    });
    y += Math.max(17.5, ...felder.map(feldHoehe));
    linie(FARBE.line); doc.line(SEITENRAND_MM, y, w - SEITENRAND_MM, y);
    beschriftung(t('address'), SEITENRAND_MM, y + 5.4, FARBE.faint, 5.9);
    F('medium', 9.2, FARBE.ink);
    const anschrift = umbrechen(meta.projectAddress || '-', INHALT_BREITE);
    anschrift.forEach((l, i) => doc.text(l, SEITENRAND_MM, y + 10.4 + i * 4.2));
    y += 13.5 + (anschrift.length - 1) * 4.2;
    linie(FARBE.line); doc.line(SEITENRAND_MM, y, w - SEITENRAND_MM, y);
    cy = y + 10;
  }

  // ============================================================================
  // Teilnehmer und Verteiler
  // ============================================================================
  // Gaeste dieser Begehung stehen in derselben Tabelle wie der Verteiler —
  // getrennt gefuehrt waeren sie im Protokoll zwei Anwesenheitslisten, und die
  // zweite wird beim Gegenlesen uebersehen. Sie sind immer anwesend (sonst gaebe
  // es sie nicht) und nie im E-Mail-Verteiler (sie haben keine Adresse).
  const guestCount = (meta.guests || []).length;
  if (printOptions.includeDistribution && ((meta.distributionList && meta.distributionList.length > 0) || guestCount > 0)) {
    abschnitt(t('participantsTitle'));
    // Feste Breite fuer die E-Mail-Spalte, damit der Umbruch nach dem @ vorab
    // entschieden werden kann - autoTable wuerde sonst mitten im Wort brechen.
    const emailColWidth = 55;
    const ja = (an: boolean) => zeichenZelle(cell => {
      const mx = cell.x + cell.width / 2, my = cell.y + cell.height / 2;
      if (an) haken(mx - 1.5, my + 0.2, 3, FARBE.brand);
      else { F('regular', 8, FARBE.faint); doc.text('–', mx, my + 1, { align: 'center' }); }
    });
    const gast = effectiveLang === 'en' ? 'Guest' : 'Gast';
    tabelle([t('colCompany'), t('colName'), t('colRole'), t('colEmail'), t('colPresentShort'), t('colDistShort')], [
        ...(meta.distributionList || []).map(c => [
          c.company || '-', c.name || '-', sanitizeRole(c.role, effectiveLang), c.email || '–', ja(!!c.present), ja(!!c.emailDistribution)
        ]),
        ...(meta.guests || []).map(g => [
          g.company || '-', g.name || '-', g.role ? `${sanitizeRole(g.role, effectiveLang)} (${gast})` : gast, '–', ja(true), ja(false)
        ]),
      ], {
      styles: { font: SCHRIFT.regular[0], fontStyle: SCHRIFT.regular[1] as 'normal', fontSize: 7.8, textColor: FARBE.text as Color, cellPadding: { top: 2.3, bottom: 2.3, left: 1.8, right: 1.8 }, lineColor: FARBE.line as Color, lineWidth: { bottom: 0.2 }, valign: 'middle' },
      columnStyles: {
        0: { cellWidth: 46, font: SCHRIFT.medium[0], fontStyle: SCHRIFT.medium[1] as 'normal', textColor: FARBE.ink as Color },
        1: { cellWidth: 21 }, 2: { cellWidth: 30 },
        3: { cellWidth: emailColWidth, fontSize: 7.2, textColor: FARBE.muted as Color },
        4: { cellWidth: 11 }, 5: { cellWidth: 11 },
      },
      didParseCell: (data) => {
        if (data.section === 'head' && data.column.index >= 4) { data.cell.styles.halign = 'center'; }
        if (data.section === 'body' && data.column.index === 3 && typeof data.cell.raw === 'string' && looksLikeEmail(data.cell.raw)) {
          F('regular', 7.2);
          data.cell.text = wrapEmail(data.cell.raw, emailColWidth - data.cell.padding('horizontal'));
        }
      },
    });
  }

  // ============================================================================
  // Allgemeine Pruefpunkte
  // ============================================================================
  if (printOptions.includeChecklists && reportToPrint.checklists && reportToPrint.checklists.length > 0) {
    const relevantChecklists = reportToPrint.checklists.filter(c => c.isRelevant);
    if (relevantChecklists.length > 0) {
      abschnitt(t('checkpoints'));
      const uniqueMap = new Map<string, ChecklistEntry>();
      const sortedByDate = [...relevantChecklists].sort((a, b) => { const dA = a.lastChecked ? new Date(a.lastChecked).getTime() : 0; const dB = b.lastChecked ? new Date(b.lastChecked).getTime() : 0; return dB - dA; });
      sortedByDate.forEach(item => { const key = `${item.category}_${item.text}`; if (!uniqueMap.has(key)) { uniqueMap.set(key, item); } });
      const finalChecklist = Array.from(uniqueMap.values()).sort((a, b) => { if (a.category !== b.category) return a.category.localeCompare(b.category); return a.text.localeCompare(b.text); });
      tabelle([t('colCategory'), t('colPoint'), t('colStatus'), t('colDateCheck'), t('colComment')],
        finalChecklist.map(c => [
          kategorieText(c.category, lang),
          pruefpunktText(c.text, lang),
          c.status === 'ok' || c.status === 'nok'
            ? zeichenZelle(cell => plakette(cell.x + 1.8, cell.y + cell.height / 2, c.status === 'ok' ? STATUS.closed : STATUS.open, c.status === 'ok' ? t('statusOK') : t('statusNOK')))
            : '–',
          formatDate(c.lastChecked) || '–',
          c.comment || '–',
        ]),
        { columnStyles: {
            0: { cellWidth: 34, fontSize: 7.6, textColor: FARBE.muted as Color },
            1: { font: SCHRIFT.medium[0], fontStyle: SCHRIFT.medium[1] as 'normal', textColor: FARBE.ink as Color },
            2: { cellWidth: 21 }, 3: { cellWidth: 21 }, 4: { cellWidth: 32 },
          } });
    }
  }

  // ============================================================================
  // Statistik: eine Zahlenleiste, darunter die Firmen
  // ============================================================================
  if (printOptions.includeStatistics && reportToPrint.items.length > 0) {
    abschnitt(t('statistics'), 45);
    const items = reportToPrint.items;
    // Gleiche Zählweise wie die Bildschirm-Auswertung (AnalysisView).
    const { open, closed, info: infoCount, good: goodCount, progressPercent } = countInspectionStats(items);
    // Beschriftungen ausschliesslich aus TRANSLATIONS: die Statuswerte hier
    // und die Plaketten auf den Mangelkarten benennen denselben Status sonst
    // unterschiedlich („Hinweis" gegen „Info").
    const werte: Array<[string, string, RGB]> = [
      [String(open), t('statusOpen'), STATUS.open.c],
      [String(infoCount), t('statusInfo'), STATUS.info.c],
      [String(goodCount), t('statusGood'), STATUS.good.c],
      [String(closed), t('statusClosed'), STATUS.closed.c],
      [`${progressPercent} %`, t('completionRate'), FARBE.ink],
    ];
    const fuenftel = INHALT_BREITE / 5;
    werte.forEach(([zahl, label, farbe], i) => {
      const x = SEITENRAND_MM + i * fuenftel + (i ? 5 : 0);
      if (i) { linie(FARBE.line); doc.line(SEITENRAND_MM + i * fuenftel, cy, SEITENRAND_MM + i * fuenftel, cy + 12.5); }
      F('semibold', 17, farbe); doc.text(zahl, x, cy + 7.2);
      F('medium', 7.4, FARBE.muted); doc.text(kuerzen(label, fuenftel - 7), x, cy + 11.8);
    });
    cy += 20;

    // Ohne „Hinweis" und „Information": beide zählten nur auf `total` und
    // drückten damit die Quote der Zeile (siehe selectItemsForCompanyStats).
    // AnalysisView filtert identisch, damit Bildschirm und Ausdruck dieselben
    // Prozente zeigen.
    // Gruppiert wird auf dem ROHEN String (nur so lässt er sich später exakt
    // zusammenführen). Getrimmt wird ausschließlich die Frage, ob überhaupt eine
    // Firma eingetragen ist — sonst zeigte der Ausdruck für einen Wert aus
    // reinen Leerzeichen eine eigene, optisch leere Zeile mehr als der
    // Bildschirm, der ihn unter „Nicht zugewiesen" führt.
    const companyStats = selectItemsForCompanyStats(items).reduce((acc, item) => { const company = item.responsiblePerson?.trim() ? item.responsiblePerson : (effectiveLang === 'de' ? 'Nicht zugewiesen' : 'Unassigned'); if (!acc[company]) acc[company] = { open: 0, closed: 0, good: 0, total: 0 }; acc[company].total++; if (item.status === 'open') acc[company].open++; if (item.status === 'closed') acc[company].closed++; if (item.status === 'good') acc[company].good++; return acc; }, {} as Record<string, { open: number; closed: number; good: number; total: number }>);
    const firmen = Object.entries(companyStats).sort(([, a], [, b]) => b.open - a.open);
    if (firmen.length > 0) {
      // Fussnote als Unterzeile: erklaert, warum eine Firma ganz fehlen kann
      // (sie traegt nur Hinweise) und warum die Quoten von der Gesamtzahl abweichen.
      platz(30);
      beschriftung(`${t('byCompany')}  ·  ${t('excludingNotes')}`, SEITENRAND_MM, cy + 1);
      cy += 4;
      tabelle([t('colCompany'), t('colTotal'), t('colOpen'), t('colDone'), t('colPositive'), t('colQuote')],
        firmen.map(([company, s]) => {
          const quote = Math.round(((s.closed + s.good) / s.total) * 100);
          return [
            company,
            String(s.total),
            { content: String(s.open), styles: s.open ? { textColor: STATUS.open.c as Color, font: SCHRIFT.semibold[0], fontStyle: SCHRIFT.semibold[1] as 'normal' } : { textColor: FARBE.faint as Color } },
            String(s.closed),
            { content: String(s.good), styles: { textColor: (s.good ? STATUS.good.c : FARBE.faint) as Color } },
            zeichenZelle(cell => {
              const bx = cell.x + 1.8, bw = 24, my = cell.y + cell.height / 2;
              fuellung(FARBE.line); doc.roundedRect(bx, my - 0.9, bw, 1.8, 0.9, 0.9, 'F');
              if (quote > 0) {
                fuellung(quote >= 75 ? STATUS.closed.c : quote >= 50 ? FARBE.brand : STATUS.open.c);
                doc.roundedRect(bx, my - 0.9, Math.max(1.8, bw * quote / 100), 1.8, 0.9, 0.9, 'F');
              }
              F('semibold', 7.6, FARBE.ink); doc.text(`${quote} %`, bx + bw + 3, my + 1);
            }),
          ];
        }),
        { styles: { font: SCHRIFT.regular[0], fontStyle: SCHRIFT.regular[1] as 'normal', fontSize: 7.8, textColor: FARBE.text as Color, cellPadding: { top: 1.9, bottom: 1.9, left: 1.8, right: 1.8 }, lineColor: FARBE.line as Color, lineWidth: { bottom: 0.2 }, valign: 'middle' },
          columnStyles: {
            0: { font: SCHRIFT.medium[0], fontStyle: SCHRIFT.medium[1] as 'normal', textColor: FARBE.ink as Color },
            1: { cellWidth: 17, halign: 'center' }, 2: { cellWidth: 17, halign: 'center' }, 3: { cellWidth: 17, halign: 'center' },
            4: { cellWidth: 17, halign: 'center' }, 5: { cellWidth: 40 },
          },
          didParseCell: kopfMittig(1, 4) });
    }
  }

  // ============================================================================
  // Gefaehrdungs-Matrix (wechselseitige Gefaehrdungen der Gewerke)
  // ============================================================================
  if (printOptions.includeHazards && reportToPrint.mutualHazards && reportToPrint.mutualHazards.length > 0) {
      neueSeite();
      abschnitt(t('hazardsMap'), 140);
      const radarCenter = { x: seitenBreite() / 2, y: cy + 48 };
      const radarRadius = 35;
      const trades = Array.from(new Set([ ...reportToPrint.mutualHazards.map(h => h.tradeSource), ...reportToPrint.mutualHazards.map(h => h.tradeAffected) ])).sort();
      const tradePoints = trades.map((name, i) => { const angle = (i / trades.length) * Math.PI * 2 - Math.PI / 2; return { name, x: radarCenter.x + radarRadius * Math.cos(angle), y: radarCenter.y + radarRadius * Math.sin(angle), angle }; });
      linie(FARBE.lineStrong, 0.5); doc.circle(radarCenter.x, radarCenter.y, radarRadius, 'S');
      linie(FARBE.line, 0.3); doc.setLineDashPattern([2, 2], 0);
      doc.circle(radarCenter.x, radarCenter.y, radarRadius * 0.75, 'S');
      doc.circle(radarCenter.x, radarCenter.y, radarRadius * 0.5, 'S');
      doc.circle(radarCenter.x, radarCenter.y, radarRadius * 0.25, 'S');
      doc.setLineDashPattern([], 0);
      tradePoints.forEach(p => { linie([241, 245, 249], 0.2); doc.line(radarCenter.x, radarCenter.y, p.x, p.y); });
      reportToPrint.mutualHazards.forEach(h => { const p1 = tradePoints.find(p => p.name === h.tradeSource); const p2 = tradePoints.find(p => p.name === h.tradeAffected); if (p1 && p2) { const color: RGB = h.severity === 'high' ? STATUS.open.c : (h.severity === 'medium' ? STATUS.info.c : FARBE.brand); linie(color, h.severity === 'high' ? 0.8 : 0.4); if (h.severity === 'low') doc.setLineDashPattern([2, 1], 0); else doc.setLineDashPattern([], 0); doc.line(p1.x, p1.y, p2.x, p2.y); if (h.severity === 'high') { const midX = (p1.x + p2.x) / 2; const midY = (p1.y + p2.y) / 2; fuellung(STATUS.open.c); doc.circle(midX, midY, 1, 'F'); } } });
      doc.setLineDashPattern([], 0);
      tradePoints.forEach(p => { fuellung(FARBE.white); linie(FARBE.brand, 0.5); doc.circle(p.x, p.y, 2, 'FD'); F('semibold', 7, FARBE.ink); const textOffset = 7; const textX = p.x + (Math.cos(p.angle) * textOffset); const textY = p.y + (Math.sin(p.angle) * textOffset); const cos = Math.cos(p.angle); let align: 'left' | 'center' | 'right' = 'center'; if (cos > 0.2) align = 'left'; else if (cos < -0.2) align = 'right'; doc.text(p.name, textX, textY, { align: align }); });
      cy = radarCenter.y + radarRadius + 22;
      const hazardRows = reportToPrint.mutualHazards.map(h => [h.tradeSource, h.tradeAffected, h.description, h.measure, h.severity.toUpperCase()]);
      tabelle([t('hazardSource'), t('hazardAffected'), t('hazardDescription'), t('hazardMeasure'), t('hazardRisk')], hazardRows, {
        columnStyles: { 4: { halign: 'center', font: SCHRIFT.semibold[0], fontStyle: SCHRIFT.semibold[1] as 'normal' } },
        didParseCell: (data) => { if (data.column.index === 4 && data.section === 'body') { const val = data.cell.raw as string; if (val === 'HIGH') data.cell.styles.textColor = STATUS.open.c; if (val === 'MEDIUM') data.cell.styles.textColor = STATUS.info.c; } if (data.section === 'head' && data.column.index === 4) data.cell.styles.halign = 'center'; },
      });
  }

  // ============================================================================
  // Mangelkarte — Statusleiste links, Nummer + Titel + Plakette, Meta-Band,
  // Beschreibung, Rechtsgrundlage, Fotos im 4:3-Raster.
  // Die Karte besteht aus Bausteinen fester Hoehe: Kopf (Titel + Meta-Band),
  // je Textzeile einer, Rechtsgrundlage, Freimeldung, je Fotoreihe einer. Passt
  // eine Karte nicht auf eine ganze Seite (sehr langer Text, zweisprachiger
  // Bericht), laeuft sie bausteinweise auf der naechsten Seite weiter — mit
  // eigenem Rahmen und Statusleiste je Seite, statt ueber den Blattrand.
  // ============================================================================
  interface Baustein { h: number; zeichne: (oben: number) => void }
  const kartenBausteine = (item: InspectionItem, nummer: number): Baustein[] => {
      // Eine „Information" ist eine Vorbemerkung an den Leser, kein Ticket:
      // sie bekommt keine #Nr, keine Verortung, keinen Zuständigen und keine
      // Frist.
      const isGeneral = item.status === 'general';
      const isClosed = item.status === 'closed';
      const sf = STATUS[item.status] ?? STATUS.open;
      const ix = SEITENRAND_MM + KARTE_LEISTE + KARTE_PAD;
      const iw = KARTE_INNEN;
      const bausteine: Baustein[] = [];

      // --- Kopf: Titelzeile und Meta-Band (wird nie geteilt) ---
      const titelOben = KARTE_PAD + (isGeneral ? 5.4 : 0);
      const nrText = isGeneral ? '' : (item.ticketNumber ? `#${item.ticketNumber}` : `#${nummer + 1}`);
      const label = statusText(item.status);
      const pw = isGeneral ? 0 : plaketteBreite(label);
      F('semibold', 10.5);
      const nw = nrText ? doc.getTextWidth(nrText) + 3 : 0;
      const titel = umbrechen(item.title || '-', iw - nw - (isGeneral ? 0 : pw + 4));
      const titelUnten = titelOben + 3.7 + (titel.length - 1) * 5 + 1.6;

      // Meta-Band: Ort | Zuständig | Festgestellt | Frist bzw. Erledigt am
      interface Feld { l: string; v: string; anteil: number; rolle?: ReportFontRole; farbe?: RGB; unter?: string; unterFarbe?: RGB }
      let felder: Feld[] = [];
      if (!isGeneral) {
          const ort: Feld = { l: t('colLocation'), v: item.location || '-', anteil: 0.42 };
          const zust: Feld = { l: t('responsible'), v: item.responsiblePerson || '-', anteil: 0.24 };
          const gef: Feld = { l: t('dateFound'), v: formatDate(item.dateFound) || '-', anteil: 0.15 };
          if (isClosed) {
              felder = [ort, zust, gef, { l: t('colDateClearance'), v: formatDate(item.closedDate) || '-', anteil: 0.19, rolle: 'semibold', farbe: STATUS.closed.c }];
          } else if (item.status === 'open') {
              // Sofortmaßnahme: statt eines Datums steht, dass die
              // Verantwortlichen informiert wurden — eine Frist gibt es nicht.
              const frist: Feld = item.immediateAction
                  ? { l: t('deadline'), v: t('immediateAction'), anteil: 0.19, rolle: 'semibold', farbe: STATUS.open.c, unter: t('immediateActionNote') }
                  : fristUeberschritten(item.deadline, meta.date)
                      ? { l: t('deadline'), v: formatDate(item.deadline), anteil: 0.19, rolle: 'semibold', farbe: STATUS.open.c, unter: t('overdue'), unterFarbe: STATUS.open.c }
                      : { l: t('deadline'), v: formatDate(item.deadline) || '-', anteil: 0.19 };
              felder = [ort, zust, gef, frist];
          } else {
              felder = [ort, zust, { ...gef, anteil: 0.34 }];
          }
      }
      const satz = felder.map(f => {
          F(f.rolle || 'medium', 8); const werte = umbrechen(f.v, f.anteil * iw - 3);
          F('regular', 6.6); const unter = f.unter ? umbrechen(f.unter, f.anteil * iw - 3) : [];
          return { werte, unter };
      });
      const bandOben = titelUnten + 2;
      const bandHoehe = felder.length
          ? Math.max(...satz.map(s => 8.6 + (s.werte.length - 1) * 3.6 + (s.unter.length ? 3.3 + (s.unter.length - 1) * 3.1 : 0))) + 2.6
          : 0;
      bausteine.push({
          h: felder.length ? bandOben + bandHoehe : titelUnten,
          zeichne: oben => {
              if (isGeneral) beschriftung(t('statusGeneral'), ix, oben + KARTE_PAD + 1.6, sf.c);
              if (nrText) { F('semibold', 10.5, sf.c); doc.text(nrText, ix, oben + titelOben + 3.7); }
              F('semibold', 10.5, FARBE.ink); titel.forEach((l, i) => doc.text(l, ix + nw, oben + titelOben + 3.7 + i * 5));
              if (!isGeneral) plakette(SEITENRAND_MM + INHALT_BREITE - KARTE_PAD - pw, oben + titelOben + 2.4, sf, label);
              if (!felder.length) return;
              const bo = oben + bandOben;
              fuellung(FARBE.surface); doc.rect(ix - 2.2, bo, iw + 4.4, bandHoehe, 'F');
              let mx = ix;
              felder.forEach((f, i) => {
                  beschriftung(f.l, mx, bo + 4.2, FARBE.faint, 5.9);
                  F(f.rolle || 'medium', 8, f.farbe || FARBE.ink);
                  satz[i].werte.forEach((l, j) => doc.text(l, mx, bo + 8.6 + j * 3.6));
                  if (satz[i].unter.length) {
                      const ub = bo + 8.6 + (satz[i].werte.length - 1) * 3.6 + 3.3;
                      F('regular', 6.6, f.unterFarbe || FARBE.muted);
                      satz[i].unter.forEach((l, j) => doc.text(l, mx, ub + j * 3.1));
                  }
                  mx += f.anteil * iw;
              });
          },
      });

      // --- Beschreibung: je Zeile ein Baustein ---
      if (item.observation) {
          F('regular', 9);
          const lh = 9 * 0.3528 * 1.45;
          umbrechen(item.observation, iw).forEach((zeile, i) => {
              // Grundlinie: erste Zeile mit Abstand zum Kopf, danach im Zeilenabstand.
              const versatz = i === 0 ? (isGeneral ? 4.2 : 5.6) : lh - 1.6;
              bausteine.push({ h: versatz + 1.6, zeichne: oben => { F('regular', 9, FARBE.text); doc.text(zeile, ix, oben + versatz); } });
          });
      }

      // --- Rechtsgrundlage (nicht bei Informationen): je Zeile ein Baustein ---
      if (item.regulation && !isGeneral) {
          const cleanReg = item.regulation.replace(/§\s+/g, '§').trim();
          F('regular', 7.2);
          umbrechen(cleanReg, iw - 6).forEach((zeile, i) => {
              const versatz = i === 0 ? 3.6 : 3.25 - 1.2;
              bausteine.push({
                  h: versatz + 1.2,
                  zeichne: oben => {
                      if (i === 0) { F('semibold', 9, FARBE.faint); doc.text('§', ix, oben + versatz + 0.1); }
                      F('regular', 7.2, FARBE.muted); doc.text(zeile, ix + 5, oben + versatz);
                  },
              });
          });
      }

      // --- Freimeldung (erledigt): Kopfzeile, dann je Kommentarzeile ein
      // Baustein; jeder fuellt seinen Streifen, so bleibt der Kasten auch
      // ueber einen Seitenwechsel geschlossen.
      if (isClosed && (item.statusComment || item.closedDate)) {
          F('regular', 7.8);
          const kommentar = item.statusComment ? umbrechen(item.statusComment, iw - 8) : [];
          const streifen = (oben: number, h: number) => { fuellung(STATUS.closed.tint); doc.rect(ix - 2.2, oben, iw + 4.4, h, 'F'); };
          const kopfH = kommentar.length ? 6.1 : 8.2;
          bausteine.push({
              h: 3 + kopfH,
              zeichne: oben => {
                  const bo = oben + 3;
                  streifen(bo, kopfH);
                  haken(ix, bo + 4.6, 2.6, STATUS.closed.c);
                  F('semibold', 7.8, STATUS.closed.c);
                  doc.text(`${t('clearanceOn')} ${formatDate(item.closedDate) || '-'}`, ix + 4.5, bo + 4.6);
              },
          });
          kommentar.forEach((zeile, i) => {
              // Die letzte Zeile traegt den unteren Innenabstand des Kastens.
              const h = 3.5 + (i === kommentar.length - 1 ? 2.1 : 0);
              bausteine.push({
                  h,
                  zeichne: oben => { streifen(oben, h); F('regular', 7.8, FARBE.text); doc.text(zeile, ix + 4.5, oben + 2.5); },
              });
          });
      }

      // --- Fotos: je Reihe (drei Bilder) ein Baustein ---
      // Historien-Karten zeichnen dieselben optimierten Bilder wie aktuelle
      // Karten. Frueher gingen hier die ROHEN Foto-URLs an jsPDF: jede
      // http-URL wurde per synchronem XHR in voller Aufloesung geladen und
      // blockierte die Oberflaeche. Die rohe Quelle bleibt nur Rueckfall,
      // falls die Optimierung scheiterte.
      const clearanceImage = isClosed ? (optimizedClosedImages.get(item.id) || item.closedImage) : undefined;
      const fotos: string[] = item.photos && item.photos.length > 0 ? optimizedPhotos.get(item.id) || item.photos : [];
      const zellen: Array<{ quelle: string; text: string; beweis?: boolean }> =
          fotos.slice(0, FOTOS_JE_KARTE).map((quelle, i) => ({ quelle, text: `${t('photo')} ${i + 1}` }));
      if (clearanceImage) zellen.push({ quelle: clearanceImage, text: t('clearanceProof'), beweis: true });
      for (let r = 0; r * 3 < zellen.length; r++) {
          const abstand = r === 0 ? 4.4 : 3.9;
          const reihe = zellen.slice(r * 3, r * 3 + 3);
          // Gemeinsame Hoehe: so hoch, dass die Breiten (Hoehe ÷ Seitenverhaeltnis)
          // zusammen die Kartenbreite fuellen — aber nie hoeher als die Obergrenze.
          const verhaeltnisse = reihe.map(z => hoeheZuBreite(z.quelle));
          const breiteJeMm = verhaeltnisse.reduce((a, v) => a + 1 / v, 0);
          const h = Math.min(KARTEN_FOTO_MAX_HOEHE_MM, (iw - (reihe.length - 1) * FOTO_ABSTAND) / breiteJeMm);
          bausteine.push({
              h: abstand + h + 3.6,
              zeichne: oben => {
                  let x = ix;
                  reihe.forEach((z, i) => {
                      const w = h / verhaeltnisse[i];
                      const fy = oben + abstand;
                      bild(z.quelle, x, fy, w, h, z.beweis ? STATUS.closed.c : FARBE.line, z.beweis ? 0.6 : 0.2);
                      F('medium', 6.4, z.beweis ? STATUS.closed.c : FARBE.muted); doc.text(kuerzen(z.text, w + FOTO_ABSTAND - 1), x, fy + h + 3.4);
                      x += w + FOTO_ABSTAND;
                  });
              },
          });
      }
      return bausteine;
  };
  const kartenHoehe = (bausteine: Baustein[]) => bausteine.reduce((a, b) => a + b.h, 0) + KARTE_PAD;
  const kartenRahmen = (oben: number, hoehe: number, s: StatusFarbe) => {
      linie(FARBE.line); doc.rect(SEITENRAND_MM, oben, INHALT_BREITE, hoehe, 'S');
      fuellung(s.c); doc.rect(SEITENRAND_MM, oben, KARTE_LEISTE, hoehe, 'F');
  };
  const hochformatFrei = () => inhaltUnten() - INHALT_OBEN;
  /**
   * Was direkt unter einer Ueberschrift Platz haben muss: die ganze Karte,
   * wenn sie mit der Ueberschrift auf eine Seite passt — sonst Kopf und erste
   * Zeile (die Karte laeuft dann ohnehin auf der naechsten Seite weiter).
   */
  const kartenMindestHoehe = (item: InspectionItem, nummer: number) => {
      const b = kartenBausteine(item, nummer);
      const ganz = kartenHoehe(b);
      return ganz + 11 <= hochformatFrei() ? ganz : b[0].h + (b[1]?.h ?? 0) + KARTE_PAD;
  };
  /**
   * Setzt eine Karte. Passt sie nicht mehr auf die Seite, beginnt sie oben auf
   * der naechsten — ausser direkt unter einer Ueberschrift (die sonst allein
   * stuende) oder wenn sie laenger als eine ganze Seite ist: dann laeuft sie
   * hier los und bausteinweise weiter.
   */
  const karteSetzen = (item: InspectionItem, nummer: number, direktNachUeberschrift = false) => {
      const bausteine = kartenBausteine(item, nummer);
      const ganz = kartenHoehe(bausteine);
      const sf = STATUS[item.status] ?? STATUS.open;
      if (cy + ganz > inhaltUnten() && !direktNachUeberschrift && ganz <= hochformatFrei()) neueSeite();
      if (cy + ganz <= inhaltUnten()) {
          let o = cy;
          bausteine.forEach(b => { b.zeichne(o); o += b.h; });
          kartenRahmen(cy, ganz, sf);
          cy += ganz + 6;
          return;
      }
      platz(bausteine[0].h + (bausteine[1]?.h ?? 0) + KARTE_PAD);
      let teilOben = cy;
      let o = cy;
      bausteine.forEach((b, i) => {
          if (i > 0 && o + b.h + KARTE_PAD > inhaltUnten()) {
              kartenRahmen(teilOben, o + KARTE_PAD - teilOben, sf);
              neueSeite();
              teilOben = cy;
              o = cy + 2;
          }
          b.zeichne(o);
          o += b.h;
      });
      kartenRahmen(teilOben, o + KARTE_PAD - teilOben, sf);
      cy = o + KARTE_PAD + 6;
  };

  /**
   * Der Kasten „Mangel online als behoben melden" — einmal pro PDF, vor dem
   * ersten Mangel.
   *
   * Steht NUR im PDF (nie im Mailtext, der sonst Spam-Punkte sammelt) und nur,
   * wenn es einen Link gibt UND ueberhaupt Maengel im Bericht stehen. Ohne Link
   * wird nichts gezeichnet, auch kein Platzhalter.
   */
  const freimeldeKasten = () => {
      const qr = options?.freimeldungQr;
      if (!qr) return;

      const QR_MM = 24;
      const PAD = 4.5;
      const textX = SEITENRAND_MM + PAD + QR_MM + 5.5;
      const textW = SEITENRAND_MM + INHALT_BREITE - PAD - textX;

      // Der Bericht hat eine Sprache — der Hinweis steht zusaetzlich in der
      // anderen, weil auf der Baustelle beide Sprachen mitlesen.
      const qrLang: 'de' | 'en' = lang === 'de' ? 'de' : 'en';
      const otherLang: 'de' | 'en' = qrLang === 'de' ? 'en' : 'de';
      // Mit Foto-Pflicht nennt der Hinweis das Foto als Pflicht statt als Option.
      const hintKey = meta.freimeldung?.fotoPflicht === true ? 'freimeldungHintFotoPflicht' : 'freimeldungHint';
      const otherHint = TRANSLATIONS[hintKey]?.[otherLang] || '';

      // Erst messen, dann zeichnen: der Rahmen braucht die endgueltige Hoehe.
      F('regular', 7.8); const hintLines = umbrechen(t(hintKey), textW);
      F('regular', 6.6); const otherLines = otherHint ? umbrechen(otherHint, textW) : [];
      F('medium', 7); const urlLines = umbrechen(qr.url, textW);
      const textHeight = 7.4 + 4.8 + hintLines.length * 3.5 + otherLines.length * 3 + 0.6 + urlLines.length * 3 + 2.5;
      const boxHeight = Math.max(QR_MM + 2 * PAD, textHeight);
      platz(boxHeight);

      linie(FARBE.brandLine, 0.35); doc.setLineDashPattern([1.4, 1], 0);
      doc.roundedRect(SEITENRAND_MM, cy, INHALT_BREITE, boxHeight, 2, 2, 'S');
      doc.setLineDashPattern([], 0);

      // Ein kaputtes Bild darf das PDF nie kippen — der Versand laeuft weiter.
      try {
          doc.addImage(qr.dataUrl, 'PNG', SEITENRAND_MM + PAD, cy + (boxHeight - QR_MM) / 2, QR_MM, QR_MM);
      } catch (e) {
          console.warn('Freimelde-QR konnte nicht ins PDF eingebettet werden', e);
      }

      let ty = cy + 7.4;
      F('semibold', 10, FARBE.ink); doc.text(t('freimeldungTitle'), textX, ty); ty += 4.8;
      F('regular', 7.8, FARBE.text); hintLines.forEach((l, i) => doc.text(l, textX, ty + i * 3.5)); ty += hintLines.length * 3.5;
      if (otherLines.length > 0) {
          F('regular', 6.6, FARBE.faint); otherLines.forEach((l, i) => doc.text(l, textX, ty + i * 3)); ty += otherLines.length * 3 + 0.6;
      }
      F('medium', 7, FARBE.brand); urlLines.forEach((l, i) => doc.text(l, textX, ty + i * 3));
      cy += boxHeight + 10;
  };

  // ============================================================================
  // Vorbemerkungen, Freimeldung, Maengel nach Dringlichkeit
  // ============================================================================
  // activeItems / historyItems stehen oben — vor der Bildoptimierung, damit
  // nur fuer tatsaechlich gezeichnete Karten Bilder gerechnet werden.
  if (activeItems.length > 0 && printOptions.includeActiveItems) {
      // Vorbemerkungen bekommen eine eigene Überschrift. Unter den Mängeln
      // gelesen wirkte eine Information wie ein Mangel. activeItems ist bereits
      // sortiert (Information zuerst), die Filter erhalten die Reihenfolge.
      const generalItems = activeItems.filter(i => i.status === 'general');
      const findingItems = activeItems.filter(i => i.status !== 'general');

      if (generalItems.length > 0) {
          abschnitt(t('preliminaryNotes'), kartenMindestHoehe(generalItems[0], 0));
          generalItems.forEach((item, idx) => karteSetzen(item, idx, idx === 0));
          cy += 3;
      }

      // Bedingung ist ein OFFENER Mangel, nicht irgendeine Feststellung:
      // `offeneItemIdsFuerBericht` (freimeldungLogic.ts) nimmt fuer die
      // Momentaufnahme des Links genau `status === 'open'`. Bei einem Bericht,
      // in dem nur noch Hinweise ('info') oder Positiv-Eintraege ('good')
      // aktiv sind, traegt der Link ein leeres `item_ids` — der gedruckte QR
      // fuehrte dann auf „Alle Maengel dieses Berichts sind erledigt."
      const freizumelden = findingItems.some(i => i.status === 'open');
      if (options?.freimeldungQr && printOptions.showFreimeldungQr !== false && freizumelden) {
          freimeldeKasten();
      }

      // Jeder Mangel steht genau einmal im Protokoll; die Gliederung traegt
      // die Dringlichkeit (Betreiber 18.10.2026). Innerhalb einer Gruppe bleibt
      // die Reihenfolge von selectActiveItemsForReport.
      const gruppen: Array<{ key: Dringlichkeit; titel: string; punkt?: RGB }> = [
          { key: 'sofort', titel: t('groupUrgent'), punkt: STATUS.open.c },
          { key: 'offen', titel: t('groupOpen') },
          { key: 'hinweise', titel: t('groupNotes') },
      ];
      gruppen.forEach(g => {
          const eintraege = findingItems.filter(i => dringlichkeit(i, meta.date) === g.key);
          if (eintraege.length === 0) return;
          abschnitt(g.titel, kartenMindestHoehe(eintraege[0], findingItems.indexOf(eintraege[0])), g.punkt);
          eintraege.forEach((item, i) => karteSetzen(item, findingItems.indexOf(item), i === 0));
          cy += 3;
      });
  }

  // ============================================================================
  // Planseiten
  // ============================================================================
  // Planseiten stehen quer. Was danach kommt, muss ausdruecklich zurueck ins
  // Hochformat (siehe Historie).
  let letzteSeiteQuer = false;
  // relevantPlans (planeFuerPdf) steht oben: dieselbe Auswahl steuert die
  // Bildoptimierung, ein Plan ohne Nadel wird gar nicht erst verkleinert.
  if (relevantPlans.length > 0) {
      // Jeder Plan auf eigener Seite A4 quer, Bild so gross wie moeglich
      // (Betreiber 28.09.2026, docs/planverortung-vertrag.md P4). Stecknadeln
      // statt Punkte; ein Mangel kann Nadeln auf mehreren Plaenen tragen.
      const KUGEL_MM = STECKNADEL_KUGEL_MM;
      const MANGEL_ROT = [220, 38, 38];
      const punktFarbe = (farbe: string | undefined): number[] => {
          if (farbe?.startsWith('#')) return hexToRgb(farbe);
          if (farbe === 'red') return [220, 38, 38];
          if (farbe === 'blue') return [37, 99, 235];
          if (farbe === 'yellow') return [234, 179, 8];
          if (farbe === 'purple') return [124, 58, 237];
          if (farbe === 'orange') return [249, 115, 22];
          return [34, 197, 94];
      };
      for (const plan of relevantPlans) {
          neueSeite('landscape');
          letzteSeiteQuer = true;
          const querBreite = doc.internal.pageSize.width;
          const querHoehe = doc.internal.pageSize.height;
          F('semibold', 11, FARBE.ink);
          doc.text(`${t('planLocation')}: ${plan.name}`, SEITENRAND_MM, PLAN_SEITE.titelY);
          try {
              const planData = optimizedPlans.get(plan.id) || plan.image;
              const imgProps = doc.getImageProperties(planData);

              const itemsOnPlan = selectItemsOnPlan(reportToPrint.items, plan.id);
              const pointsOnPlan = reportToPrint.safetyPoints?.filter(p => p.planId === plan.id) || [];
              // Blickrichtung aus der Sensor-Verortung (Grad im Uhrzeigersinn ab
              // Plan-oben): nur an der ausdruecklich markierten Sensor-Nadel
              // (planPins) — weitere Nadeln sind von Hand gesetzt.
              const blickVon = (item: typeof itemsOnPlan[number], sensor: boolean | undefined): number | null => {
                  const blick = item.verortung?.blickGradPlan;
                  return sensor && typeof blick === 'number' && Number.isFinite(blick) ? blick : null;
              };
              // Alle Nadeln DIESES Plans (Plan-Prozent): Maengel (planPins) und
              // Sicherheitspunkte gemeinsam — nur so faechern nahe Nadeln beider
              // Arten auf und stapeln sich richtig.
              const nadeln: Array<{
                  px: number; py: number; farbe: readonly number[];
                  nummer: string | number | null | undefined; beschriftung: string | null; blick: number | null;
              }> = [
                  ...itemsOnPlan.flatMap(item => pinsAufPlan(item, plan.id).map(pin => ({
                      px: pin.x, py: pin.y, farbe: MANGEL_ROT, nummer: item.ticketNumber,
                      beschriftung: null, blick: blickVon(item, pin.sensor),
                  }))),
                  ...pointsOnPlan.map(p => ({
                      px: p.x, py: p.y, farbe: punktFarbe(p.color), nummer: p.number || null,
                      beschriftung: p.label || null, blick: null,
                  })),
              ];

              // Nadeln nah am oberen Planrand duerfen nicht in die Titelzeile
              // ragen. Die Neigung haengt von den Abstaenden in mm ab, also von
              // der Bildgroesse — und die erst vom Ueberstand. Deshalb hier die
              // Obergrenze fuer jede Neigung (senkrecht = am hoechsten): der
              // Rahmen ist damit sicher, egal wie die Nadeln danach fallen;
              // schlimmstenfalls rueckt ein Plan mit Nadeln ganz oben um den
              // Unterschied L·(1 − cos θ) weiter nach unten (≈ 0,2 mm bei 15°,
              // bis ~3 mm bei stark aufgefaecherten Gruppen) — nie ins Titelfeld.
              const rahmen = planBildRahmenMitNadeln(querBreite, querHoehe, imgProps.width, imgProps.height, SEITENRAND_MM,
                  nadeln.map(n => ({ yProzent: n.py, ueberstand: nadelUeberstandOben(KUGEL_MM, !!n.beschriftung, null, n.blick) })),
                  PLAN_SEITE.untenFrei + FUSS_ZUSATZ_QUER);
              if (!rahmen) continue;
              const { x: planX, y: planY, w, h } = rahmen;
              doc.addImage(planData, 'JPEG', planX, planY, w, h, undefined, 'FAST');
              const aufBlatt = nadeln.map(n => ({ ...n, x: planX + (n.px / 100) * w, y: planY + (n.py / 100) * h }));
              // Neigung aus den Spitzen in mm: einzeln leicht schraeg, nahe beieinander aufgefaechert.
              const neigungen = nadelNeigungen(aufBlatt, STECKNADEL_GRUPPEN_FAKTOR * KUGEL_MM);
              const gesteckt = aufBlatt.map((n, i) => ({ ...n, neigung: neigungen[i], geo: stecknadelGeometrie(n.x, n.y, KUGEL_MM, neigungen[i]) }));

              // 1. Alle Schatten zuerst: kein Schatten faellt auf eine Kugel.
              gesteckt.forEach(n => zeichneStecknadelSchatten(doc, n.geo));
              // 2. Blickpfeile: beginnen an der Spitze und liegen UNTER allen
              // Nadeln, damit Kugel und Nummer lesbar bleiben; um den von der
              // Kugel verdeckten Teil verlaengert.
              gesteckt.forEach(({ blick, x, y, neigung }) => {
                  if (blick === null) return;
                  const pfeil = blickpfeil(x, y, blick, blickpfeilLaengeAnNadel(blick, KUGEL_MM, neigung, 7));
                  doc.setDrawColor(MANGEL_ROT[0], MANGEL_ROT[1], MANGEL_ROT[2]); doc.setLineWidth(BLICKPFEIL_STRICH_MM);
                  doc.line(x, y, pfeil.spitze.x, pfeil.spitze.y);
                  doc.setFillColor(MANGEL_ROT[0], MANGEL_ROT[1], MANGEL_ROT[2]);
                  doc.triangle(pfeil.spitze.x, pfeil.spitze.y, pfeil.links.x, pfeil.links.y, pfeil.rechts.x, pfeil.rechts.y, 'F');
              });
              // 3. Nadeln von oben nach unten: die tiefer steckende liegt vorn.
              [...gesteckt].sort((a, b) => a.y - b.y).forEach(n => {
                  zeichneStecknadel(doc, n.x, n.y, KUGEL_MM, n.farbe, n.nummer, n.beschriftung, n.neigung);
              });
          } catch (e) { console.error("Error adding plan to PDF", e); }
      }
  }

  // ============================================================================
  // Erledigte Maengel (Historie)
  // ============================================================================
  if (historyItems.length > 0 && printOptions.includeHistory) {
      // Nach den Planseiten ausdruecklich wieder Hochformat: alles Folgende
      // rechnet mit der Hochformat-Breite.
      if (letzteSeiteQuer) neueSeite('portrait');
      // historyWithProof (Karten) / historySimple (Tabellenzeilen) stehen oben,
      // aufgeteilt von teileErledigteFuerPdf — Betreiber-Entscheid 30.09.2026:
      // Erledigte vor dem Vorbericht nur noch als Zeile ohne Bild.
      const ersteKarte = historyWithProof[0];
      abschnitt(t('historyTitle'), historySimple.length > 0 || !ersteKarte ? 30 : kartenMindestHoehe(ersteKarte, 0));
      if (historySimple.length > 0) {
          tabelle(['#', t('colTitle'), t('clearance'), t('colDateClearance')],
            historySimple.map(item => [
              { content: item.ticketNumber ? `#${item.ticketNumber}` : '-', styles: { font: SCHRIFT.semibold[0], fontStyle: SCHRIFT.semibold[1] as 'normal', textColor: STATUS.closed.c as Color } },
              item.responsiblePerson
                ? zeilenZelle([{ t: item.title || '-', rolle: 'medium', size: 8.2, farbe: FARBE.ink }, { t: item.responsiblePerson, rolle: 'regular', size: 7, farbe: FARBE.muted }])
                : zeilenZelle([{ t: item.title || '-', rolle: 'medium', size: 8.2, farbe: FARBE.ink }]),
              { content: item.statusComment || '–', styles: { fontSize: 7.6 } },
              formatDate(item.closedDate) || '–',
            ]),
            { columnStyles: { 0: { cellWidth: 14 }, 1: { cellWidth: 72 }, 3: { cellWidth: 24 } } });
      }
      if (historyWithProof.length > 0) {
          if (historySimple.length > 0) {
              // Die Zwischenzeile wirkt wie eine Ueberschrift: nie allein am Seitenende.
              cy -= 4;
              platz(7 + kartenMindestHoehe(ersteKarte, 0));
              F('regular', 9, FARBE.muted); doc.text(t('closedWithProof'), SEITENRAND_MM, cy + 3);
              cy += 7;
          }
          historyWithProof.forEach((item, idx) => karteSetzen(item, idx, idx === 0));
      }
  }

  // ============================================================================
  // Fusszeile auf jeder Seite
  // ============================================================================
  const pageCountFinal = doc.getNumberOfPages();
  for(let i = 1; i <= pageCountFinal; i++) {
    doc.setPage(i);
    const currentW = doc.internal.pageSize.width;
    const currentH = doc.internal.pageSize.height;
    // footerText steht oben: dort wird auch die Hoehe reserviert.
    const lines = fussZeilen(currentW);
    const textY = currentH - 8.5 - Math.max(0, lines.length - 1) * FUSS_ZEILENABSTAND;
    linie(FARBE.line); doc.line(SEITENRAND_MM, textY - 4.5, currentW - SEITENRAND_MM, textY - 4.5);
    F('regular', 6.8, FARBE.muted); lines.forEach((l, j) => doc.text(l, SEITENRAND_MM, textY + j * FUSS_ZEILENABSTAND));
    F('medium', 6.8, FARBE.text); doc.text(`${t('pageLabel')} ${i} / ${pageCountFinal}`, currentW - SEITENRAND_MM, currentH - 8.5, { align: 'right' });
  }
  return doc;
};
